import { useEffect, useRef } from 'react';

interface CharacterContextMenuProps {
  onClose: () => void;
  onOpen: () => void;
  onDuplicate: () => void;
  onRename: () => void;
  onExportJson: () => void;
  onExportPdf: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

/**
 * Context menu (kebab menu) for character card actions.
 */
export function CharacterContextMenu({
  onClose,
  onOpen,
  onDuplicate,
  onRename,
  onExportJson,
  onExportPdf,
  onDelete,
  isDeleting,
}: CharacterContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close menu on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const menuItems = [
    { label: 'Open', onClick: onOpen, icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
    { label: 'Duplicate', onClick: onDuplicate, icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' },
    { label: 'Rename', onClick: onRename, icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
    { label: 'Export JSON', onClick: onExportJson, icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' },
    { label: 'Export PDF', onClick: onExportPdf, icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  ];

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Character actions"
      className="absolute right-0 top-8 z-50 w-40 overflow-hidden rounded-lg border border-slate-600 bg-slate-800 shadow-xl"
    >
      <div className="py-1">
        {menuItems.map((item) => (
          <button
            key={item.label}
            role="menuitem"
            onClick={item.onClick}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700"
          >
            <svg
              className="h-4 w-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={item.icon}
              />
            </svg>
            {item.label}
          </button>
        ))}

        <div className="my-1 border-t border-slate-700" />

        <button
          role="menuitem"
          onClick={onDelete}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
            isDeleting
              ? 'bg-red-600 text-white'
              : 'text-red-400 hover:bg-red-900/30'
          }`}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          {isDeleting ? 'Click to confirm' : 'Delete'}
        </button>
      </div>
    </div>
  );
}
